/**
 * Конфигурация сущностей графа для настроек.
 * Агенты — из graph_agents, узлы — rule-based + IO, связи — state transitions.
 */

/** Агенты (LLM) — ключи из graph_agents. Порядок = порядок табов в конфигурации. */
export const AGENT_KEYS = [
  'message_analyzer',
  'photo_analyzer',
  'memory_summary',
  'message_generator',
  'photo_generator',
  'offer_generator',
  'policy_safety',
  'proactive_message_generator',
]

/** Pipeline: debounce, TTL (context и conversation — в агентах). */
export const PIPELINE_SETTING_KEYS = [
  'bot_debounce_seconds',
  'bot_running_graph_ttl_seconds',
  'bot_pending_task_ttl_seconds',
  'bot_delayed_send_ttl_seconds',
  'bot_save_retry_delay_seconds',
  'bot_save_retry_max_attempts',
]

/** Агенты, которым передаётся переписка: ключ настройки количества сообщений. */
export const AGENT_CONVERSATION_MESSAGES_KEYS = {
  message_analyzer: 'message_analyzer_conversation_messages_count',
  message_generator: 'message_generator_conversation_messages_count',
  memory_summary: 'memory_summary_conversation_messages_count',
  offer_generator: 'offer_generator_conversation_messages_count',
  proactive_message_generator: 'proactive_message_generator_conversation_messages_count',
}

/** Настройки Media Selector. */
export const MEDIA_SELECTOR_SETTING_KEYS = [
  'bot_session_switch_min_hours',
  'bot_chat_media_recent_days',
]

/** Ключи исполнителя: доставка и пороги, которые читает бэкенд (см. graph_config_service). */
export const EXECUTOR_SETTING_KEYS = [
  'bot_send_delay_min_ms',
  'bot_send_delay_max_ms',
  'bot_send_delay_default_ms',
  'bot_typing_indicator_enabled',
  'bot_send_retry_attempts',
  'bot_send_retry_backoff_ms',
  'bot_send_split_enabled',
  'bot_send_split_max_chunk_length',
  'bot_send_split_inter_chunk_delay_seconds',
  'bot_send_split_max_chunks',
  'bot_chat_media_recent_days',
  'bot_session_switch_min_hours',
  'bot_send_strict_paid_invariants',
  'bot_policy_safety_enabled',
  'timewaster_strategy_enabled',
  'timewaster_min_fan_messages',
  'timewaster_high_tension_min',
  'timewaster_low_spend_intent_max',
  'timewaster_reply_delay_min_seconds',
  'timewaster_reply_delay_max_seconds',
]

/** Узлы (не агенты): rule-based и IO. Настройки привязаны к конкретной ноде. */
export const NODES = [
  {
    key: 'in',
    labelKey: 'settings.node.label.in',
    label: 'Входящее сообщение',
    hasSettings: true,
    settingKeys: [
      'bot_debounce_seconds',
      'bot_running_graph_ttl_seconds',
      'bot_pending_task_ttl_seconds',
      'bot_delayed_send_ttl_seconds',
      'bot_save_retry_delay_seconds',
      'bot_save_retry_max_attempts',
    ],
    descriptionKey: 'settings.nodeDesc.in',
  },
  {
    key: 'core',
    labelKey: 'settings.node.label.core',
    label: 'Ядро',
    hasSettings: true,
    settingKeys: [
      'policy_risk_safety_lock_threshold',
      'bot_core_score_delta_scale',
      'bot_core_max_delta',
    ],
    descriptionKey: 'settings.nodeDesc.core',
  },
  {
    key: 'media_selector',
    labelKey: 'settings.node.label.media_selector',
    label: 'Media Selector',
    hasSettings: true,
    settingKeys: [...MEDIA_SELECTOR_SETTING_KEYS],
    descriptionKey: 'settings.nodeDesc.media_selector',
  },
  {
    key: 'send',
    labelKey: 'settings.node.label.send',
    label: 'Отправка',
    hasSettings: true,
    settingKeys: [
      'bot_send_delay_min_ms',
      'bot_send_delay_max_ms',
      'bot_send_delay_default_ms',
      'bot_typing_indicator_enabled',
      'bot_typing_duration_seconds',
      'bot_typing_interval_seconds',
      'bot_typing_chars_per_call',
      'bot_send_retry_attempts',
      'bot_send_retry_backoff_ms',
      'bot_send_split_enabled',
      'bot_send_split_max_chunk_length',
      'bot_send_split_inter_chunk_delay_seconds',
      'bot_send_split_max_chunks',
    ],
    descriptionKey: 'settings.nodeDesc.send',
  },
]

/** Ключи настроек проактивного бота — только то, что check_followup_needed использует для решения «кому писать». */
export const PROACTIVE_SETTING_KEYS = [
  'bot_silence_followup_rapport_hours',
  'bot_silence_followup_rapport_returned_hours',
  'bot_silence_followup_flirt_hours',
  'bot_silence_followup_presell_hours',
  'bot_silence_followup_offer_hours',
  'bot_silence_followup_postbuy_hours',
  'bot_silence_followup_cooldown_hours',
]

/** Ключи, интегрированные в Ядро (табы allow_offer, counters). */
export const CORE_INLINE_SETTING_KEYS = []

/** Структура JSON вывода анализатора — для отображения в UI. */
export const MESSAGE_ANALYZER_OUTPUT_SCHEMA = [
  { key: 'intent', type: 'enum', values: 'greeting, small_talk, flirt, compliment, sexual_escalation, question, emotional_disclosure, buy_signal, price_check, objection, request_content, reengagement, complaint, test_realness', descKey: 'settings.prompt.payload.ma.output.intent' },
  { key: 'tone', type: 'enum', values: 'neutral, warm, playful, flirty, horny, needy, defensive, frustrated, cold', descKey: 'settings.prompt.payload.ma.output.tone' },
  { key: 'trust_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.trust_score' },
  { key: 'attachment_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.attachment_score' },
  { key: 'sexual_tension_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.sexual_tension_score' },
  { key: 'spending_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.spending_score' },
  { key: 'resistance_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.resistance_score' },
  { key: 'user_arousal_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.user_arousal_score' },
  { key: 'user_is_alone_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.user_is_alone_score' },
  { key: 'user_privacy_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.user_privacy_score' },
  { key: 'user_has_time_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.user_has_time_score' },
  { key: 'buy_signal', type: 'bool', descKey: 'settings.prompt.payload.ma.output.buy_signal' },
  { key: 'realness_test_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.realness_test_score' },
  { key: 'policy_risk_score', type: 'int', range: '0-100', descKey: 'settings.prompt.payload.ma.output.policy_risk_score' },
  { key: 'risk_reason', type: 'string', descKey: 'settings.prompt.payload.ma.output.risk_reason' },
]

/** Схема как объект для отображения в UI (поле → тип/диапазон). */
export const MESSAGE_ANALYZER_OUTPUT_SCHEMA_AS_OBJECT = Object.fromEntries(
  MESSAGE_ANALYZER_OUTPUT_SCHEMA.map((row) => {
    const val = row.type === 'enum' ? `enum: ${row.values}` : row.type === 'int' ? `int ${row.range || ''}` : row.type === 'bool' ? 'bool' : row.type
    return [row.key, val]
  })
)

/** Структура JSON ответа message_generator — передаётся в user-промпт, LLM возвращает этот формат. */
export const MESSAGE_GENERATOR_OUTPUT_SCHEMA_AS_OBJECT = {
  text: 'string (required)',
  suggested_delay_seconds: 'int? (optional, seconds until send)',
}

/** Структура JSON ответа offer_generator — описание медиа и цена для продажи. */
export const OFFER_GENERATOR_OUTPUT_SCHEMA_AS_OBJECT = {
  caption: 'string (required)',
  price: 'int (required, cents)',
}

/** Формат ответа proactive_message_generator — plain text. */
export const PROACTIVE_MESSAGE_GENERATOR_OUTPUT_DESC = 'plain text (message to send)'

/** Маппинг агентов-генераторов на их output schema (объект) или описание (строка). */
export const GENERATOR_OUTPUT_SCHEMAS = {
  message_generator: MESSAGE_GENERATOR_OUTPUT_SCHEMA_AS_OBJECT,
  photo_generator: MESSAGE_GENERATOR_OUTPUT_SCHEMA_AS_OBJECT,
  offer_generator: OFFER_GENERATOR_OUTPUT_SCHEMA_AS_OBJECT,
  proactive_message_generator: PROACTIVE_MESSAGE_GENERATOR_OUTPUT_DESC,
}

/** Структура payload в user-промпте. Значения — ключи переводов для описаний. */
export const USER_PROMPT_PAYLOAD_SCHEMAS = {
  message_analyzer: {
    incoming_messages: 'settings.prompt.payload.ma.incoming_messages',
    conversation_history: 'settings.prompt.payload.ma.conversation_history',
    conversation_messages_count: 'settings.prompt.payload.ma.conversation_messages_count',
  },
  message_generator: {
    user_profile: 'settings.prompt.payload.sg.user_profile',
    incoming_messages: 'settings.prompt.payload.sg.incoming_messages',
    model_persona: 'settings.prompt.payload.sg.model_persona',
    recent_messages: 'settings.prompt.payload.sg.recent_messages',
    media_hint: 'settings.prompt.payload.sg.media_hint',
    send_delay_min_seconds: 'settings.prompt.payload.sg.send_delay_min_seconds',
    send_delay_max_seconds: 'settings.prompt.payload.sg.send_delay_max_seconds',
  },
  photo_analyzer: {
    incoming_messages: 'settings.prompt.payload.ma.incoming_messages',
    conversation_history: 'settings.prompt.payload.ma.conversation_history',
    conversation_messages_count: 'settings.prompt.payload.ma.conversation_messages_count',
  },
  photo_generator: {
    user_profile: 'settings.prompt.payload.sg.user_profile',
    incoming_messages: 'settings.prompt.payload.sg.incoming_messages',
    model_persona: 'settings.prompt.payload.sg.model_persona',
    recent_messages: 'settings.prompt.payload.sg.recent_messages',
    media_hint: 'settings.prompt.payload.sg.media_hint',
    send_delay_min_seconds: 'settings.prompt.payload.sg.send_delay_min_seconds',
    send_delay_max_seconds: 'settings.prompt.payload.sg.send_delay_max_seconds',
  },
  offer_generator: {
    user_profile: 'settings.prompt.payload.og.user_profile',
    media_info: 'settings.prompt.payload.og.media_info',
    model_persona: 'settings.prompt.payload.og.model_persona',
    incoming_messages: 'settings.prompt.payload.og.incoming_messages',
  },
  memory_summary: {
    existing_user_profile: 'settings.prompt.payload.ms.existing_user_profile',
    recent_messages: 'settings.prompt.payload.ms.recent_messages',
  },
  policy_safety: {
    candidate_reply_preview: 'settings.prompt.payload.ps.candidate_reply_preview',
    explicit_level: 'settings.prompt.payload.ps.explicit_level',
    tone_style: 'settings.prompt.payload.ps.tone_style',
    message_length: 'settings.prompt.payload.ps.message_length',
  },
  proactive_message_generator: {
    state: 'settings.prompt.payload.pmg.state',
    recent_messages: 'settings.prompt.payload.pmg.recent_messages',
    model_persona: 'settings.prompt.payload.pmg.model_persona',
    tone_style: 'settings.prompt.payload.pmg.tone_style',
    explicit_level: 'settings.prompt.payload.pmg.explicit_level',
  },
}

/** L3 — захардкоженные параметры Ядра. Расчёт на основе L1 (анализатор) + предыдущие params. */
export const L3_SCHEMA = [
  { key: 'engagement_score', type: 'int', range: '0–100', purposeKey: 'params.engagement_score', calcDescKey: 'settings.variables.l3.calc.engagement_score' },
  { key: 'emotional_attachment_score', type: 'int', range: '0–100', purposeKey: 'params.emotional_attachment_score', calcDescKey: 'settings.variables.l3.calc.emotional_attachment_score' },
  { key: 'sexual_tension_score', type: 'int', range: '0–100', purposeKey: 'params.sexual_tension_score', calcDescKey: 'settings.variables.l3.calc.sexual_tension_score' },
  { key: 'spending_intent_score', type: 'int', range: '0–100', purposeKey: 'params.spending_intent_score', calcDescKey: 'settings.variables.l3.calc.spending_intent_score' },
  { key: 'resistance_level', type: 'int', range: '0–100', purposeKey: 'params.resistance_level', calcDescKey: 'settings.variables.l3.calc.resistance_level' },
  { key: 'price_sensitivity', type: 'int', range: '0–100', purposeKey: 'params.price_sensitivity', calcDescKey: 'settings.variables.l3.calc.price_sensitivity' },
  { key: 'risk_of_churn', type: 'int', range: '0–100', purposeKey: 'params.risk_of_churn', calcDescKey: 'settings.variables.l3.calc.risk_of_churn' },
  { key: 'content_readiness_score', type: 'int', range: '0–100', purposeKey: 'params.content_readiness_score', calcDescKey: 'settings.variables.l3.calc.content_readiness_score' },
  { key: 'intimacy_score', type: 'int', range: '0–100', purposeKey: 'params.intimacy_score', calcDescKey: 'settings.variables.l3.calc.intimacy_score' },
]

/** Производные значения Ядра — считаются из params и конфига. */
export const CORE_DERIVED_SCHEMA = [
  { key: 'allow_offer', purposeKey: 'settings.variables.l3.derived.allow_offer', calcDescKey: 'settings.variables.l3.derived.allow_offer.calc' },
  { key: 'risk_flag', purposeKey: 'settings.variables.l3.derived.risk_flag', calcDescKey: 'settings.variables.l3.derived.risk_flag.calc' },
]

/** L2-переменные: read-only, считаются при каждом запуске графа. */
export const L2_SCHEMA = [
  { key: 'total_messages_in_chat', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.total_messages_in_chat' },
  { key: 'total_fan_messages', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.total_fan_messages' },
  { key: 'messages_in_current_session', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.messages_in_current_session' },
  { key: 'session_length_minutes', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.session_length_minutes' },
  { key: 'messages_7d', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.messages_7d' },
  { key: 'days_since_last_message', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.days_since_last_message' },
  { key: 'total_spend', type: 'float', range: '0..∞', descKey: 'settings.variables.l2.total_spend' },
  { key: 'spend_30d', type: 'float', range: '0..∞', descKey: 'settings.variables.l2.spend_30d' },
  { key: 'purchase_count', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.purchase_count' },
  { key: 'ever_bought', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.ever_bought' },
  { key: 'days_since_last_purchase', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.days_since_last_purchase' },
  { key: 'offer_accept_rate', type: 'float', range: '0..1', descKey: 'settings.variables.l2.offer_accept_rate' },
  { key: 'ghost_after_offer_rate', type: 'float', range: '0..1', descKey: 'settings.variables.l2.ghost_after_offer_rate' },
  { key: 'recent_offer_rejected', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.recent_offer_rejected' },
  { key: 'preferred_tone', type: 'enum', range: 'playful, romantic, teasing, direct, unknown', descKey: 'settings.variables.l2.preferred_tone' },
  { key: 'content_preferences', type: 'list', range: 'text[]', descKey: 'settings.variables.l2.content_preferences' },
  { key: 'buyer_style_hint', type: 'enum', range: 'impulsive, careful, price_sensitive, unknown', descKey: 'settings.variables.l2.buyer_style_hint' },
  { key: 'conversation_style_hint', type: 'enum', range: 'chatty, direct, romantic, testing, unknown', descKey: 'settings.variables.l2.conversation_style_hint' },
  { key: 'messages_since_offer', type: 'int', range: '0..∞', descKey: 'settings.variables.l2.messages_since_offer' },
  /* L4: стадии и флаги из fan_bot_profiles */
  { key: 'relationship_stage', type: 'enum', range: 'new, warm, flirty, attached', descKey: 'settings.variables.l2.relationship_stage' },
  { key: 'monetization_stage', type: 'enum', range: 'cold, warm, buyer', descKey: 'settings.variables.l2.monetization_stage' },
  { key: 'conversation_state', type: 'enum', range: 'RAPPORT, FLIRT, PRESELL, OFFER, RETENTION, REENGAGE', descKey: 'settings.variables.l2.conversation_state' },
  { key: 'sale_script_allowed', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.sale_script_allowed' },
  { key: 'direct_offer_allowed', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.direct_offer_allowed' },
  { key: 'upsell_allowed', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.upsell_allowed' },
  { key: 'cooldown_after_offer', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.cooldown_after_offer' },
  {
    key: 'current_state',
    type: 'enum',
    range: 'RAPPORT, FLIRT, PRESELL, OFFER, POSTBUY, RETENTION, REENGAGE, COOLDOWN',
    descKey: 'settings.variables.l2.current_state',
  },
  {
    key: 'postbuy_phase',
    type: 'enum',
    range: 'delivery, payment_pending, aftercare',
    descKey: 'settings.variables.l2.postbuy_phase',
  },
  {
    key: 'proactive_trigger',
    type: 'enum',
    range: 'silence, users_online, users_typing, posts_liked, posts_commented',
    descKey: 'settings.variables.l2.proactive_trigger',
  },
  { key: 'fan_came_online', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.fan_came_online' },
  { key: 'fan_is_online', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.fan_is_online' },
  { key: 'fan_started_typing', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.fan_started_typing' },
  { key: 'fan_liked_post', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.fan_liked_post' },
  { key: 'fan_commented_post', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.fan_commented_post' },
  { key: 'physical_item_interest_score', type: 'int', range: '0–100', descKey: 'settings.variables.l2.physical_item_interest_score' },
  { key: 'physical_item_active', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.physical_item_active' },
  { key: 'support_interest_score', type: 'int', range: '0–100', descKey: 'settings.variables.l2.support_interest_score' },
  { key: 'support_route_active', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.support_route_active' },
  { key: 'dominant_style_score', type: 'int', range: '0–100', descKey: 'settings.variables.l2.dominant_style_score' },
  { key: 'dominant_style_preference', type: 'enum', range: 'none, model_dominant, model_submissive, stop, unclear', descKey: 'settings.variables.l2.dominant_style_preference' },
  { key: 'dominant_style_active', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.dominant_style_active' },
  { key: 'timewaster_strategy_active', type: 'bool', range: 'true/false', descKey: 'settings.variables.l2.timewaster_strategy_active' },
]

/** Маппинг nodeKey диаграммы → route key */
export const DIAGRAM_NODE_TO_KEY = {
  IN: 'in',
  SEND: 'send',
  CB: 'core',
  MEDIA: 'media_selector',
  MA: 'message_analyzer',
  SG: 'message_generator',
  PS: 'policy_safety',
  MS: 'memory_summary',
  OG: 'offer_generator',
  PMG: 'proactive_message_generator',
  PROACTIVE: 'proactive',
}
